import { useState, useEffect } from "react";
import { Upload, FileSpreadsheet, Loader2, Download, Calculator, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Schedule III Division I — Trial Balance → Balance Sheet + P&L generator.
 *
 * Flow:
 *   1. User picks a Company from their Companies master list
 *   2. Picks up to 2 directors who will sign (from that company's directors)
 *   3. Uploads TB (xlsx/csv/pdf) + enters BS date
 *   4. Backend pulls address + auditor (Professional with role=Statutory
 *      Auditor) from the company's master record and stamps them in the
 *      workbook header/footer.
 *   5. Backend also persists a CompanyFinancials snapshot so downstream
 *      filings (Directors Report, MGT-7, AOC-4) can reuse the numbers.
 */
export default function Schedule3Page() {
  const [file, setFile] = useState(null);
  const [priorFile, setPriorFile] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [selectedDirectorDins, setSelectedDirectorDins] = useState([]);
  const [bsDate, setBsDate] = useState("31st March, 2026");
  const [busy, setBusy] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);
  const [latestTallySync, setLatestTallySync] = useState(null);

  const selectedCompany = companies.find((c) => c.id === companyId) || null;

  useEffect(() => {
    // Surface the latest Tally push (if any) right at the top of /schedule3
    axios.get(`${API}/integrations/tally/latest`, { withCredentials: true })
      .then((r) => { if (r.data && r.data.job_id) setLatestTallySync(r.data); })
      .catch(() => { /* not signed in / no push — silent */ });
    // Load the user's companies for the dropdown
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCompanies([]));
  }, []);

  // Auto-pre-select first 2 directors whenever the company changes
  useEffect(() => {
    if (!selectedCompany) { setSelectedDirectorDins([]); return; }
    const firstTwo = (selectedCompany.directors || [])
      .filter((d) => (d.din || "").trim())
      .slice(0, 2)
      .map((d) => d.din);
    setSelectedDirectorDins(firstTwo);
  }, [companyId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDirector = (din) => {
    setSelectedDirectorDins((prev) => {
      if (prev.includes(din)) return prev.filter((x) => x !== din);
      if (prev.length >= 2) return prev;  // cap at 2 signatories
      return [...prev, din];
    });
  };

  const handleGenerate = async () => {
    if (!file) {
      toast.error("Please choose a Trial Balance file (XLSX, CSV or PDF)");
      return;
    }
    if (!selectedCompany) {
      toast.error("Please select a company from your Companies list");
      return;
    }
    setBusy(true);
    setLastSummary(null);
    try {
      const form = new FormData();
      form.append("tb_file", file);
      form.append("company_name", selectedCompany.name);
      form.append("balance_sheet_date", bsDate);
      form.append("company_id", selectedCompany.id);
      if (selectedDirectorDins.length > 0) {
        form.append("signing_director_dins", selectedDirectorDins.join(","));
      }
      if (priorFile) form.append("prior_tb_file", priorFile);
      const r = await axios.post(`${API}/schedule3/generate`, form, {
        responseType: "blob",
        withCredentials: true,
      });
      // Trigger download
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const safeCo = selectedCompany.name.replace(/[^A-Za-z0-9]+/g, "_").slice(0, 50) || "company";
      const a = document.createElement("a");
      a.href = url;
      a.download = `schedule3_${safeCo}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      setLastSummary({
        pbt: r.headers["x-profit-before-tax"],
        pat: r.headers["x-profit-after-tax"],
        pbtPrev: r.headers["x-profit-before-tax-prev"],
        patPrev: r.headers["x-profit-after-tax-prev"],
        rows: r.headers["x-rows-classified"],
        rowsPrev: r.headers["x-rows-classified-prev"],
        companyId: selectedCompany.id,
      });
      toast.success("Schedule III workbook downloaded — saved to company financials.");
    } catch (e) {
      let msg = "Generation failed";
      if (e?.response?.data) {
        try {
          const text = await e.response.data.text();
          msg = JSON.parse(text).detail || text;
        } catch {
          msg = "Generation failed (HTTP " + e.response.status + ")";
        }
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="schedule3-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          <Calculator size={14} className="text-[#002FA7]" /> Schedule III · Division I
        </div>
        <h1 className="font-semibold text-2xl sm:text-3xl tracking-tight mt-2 text-zinc-950">
          Trial Balance → Balance Sheet &amp; P&amp;L
        </h1>
        <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
          Upload your Trial Balance from Tally / Zoho / Excel — ROC Vault classifies every
          ledger to the correct Schedule III Division I line item and exports a 7-sheet
          workbook: Balance Sheet, P&amp;L, Notes to Accounts, Trade Receivables &amp; Payables
          Ageing, 11 mandatory Ratios (with &gt; 25 % variance flagging), and a TB classification
          audit sheet. Optionally upload last year's TB to populate the &quot;Previous Year&quot; column.
        </p>
        <a
          href="/integrations/tally"
          className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 border border-[#002FA7] text-[#002FA7] text-xs font-medium hover:bg-blue-50/60"
          data-testid="link-tally-integration"
        >
          ⚡ Skip the manual upload — push directly from Tally Prime in one click
        </a>
      </div>

      {/* Latest Tally sync banner — shown when user has pushed at least once */}
      {latestTallySync && (
        <div className="mb-6 border border-blue-200 bg-blue-50/40 px-4 py-3" data-testid="schedule3-latest-tally">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#002FA7] font-medium">
            <CheckCircle2 size={12} /> Latest Tally sync
          </div>
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs text-zinc-700">
            <div><span className="text-zinc-500">Company: </span><b>{latestTallySync.company_name || "—"}</b></div>
            <div><span className="text-zinc-500">Ledgers: </span><b>{latestTallySync.row_count}</b></div>
            <div><span className="text-zinc-500">Pushed: </span><b>{new Date(latestTallySync.pushed_at).toLocaleString("en-IN")}</b></div>
            <div>
              <a
                href={`${API}/integrations/tally/download/${latestTallySync.job_id}`}
                data-testid="schedule3-download-latest-tally"
                className="text-[#002FA7] font-medium inline-flex items-center gap-1"
                download
              >
                <Download size={11} /> Download workbook
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 bg-white p-6 sm:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="company_id" className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
              Company <span className="text-red-500">*</span>
            </label>
            <select
              id="company_id"
              data-testid="sched3-company-select"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none bg-white"
            >
              <option value="">— Choose from your Companies list —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.cin ? ` · ${c.cin}` : ""}</option>
              ))}
            </select>
            {companies.length === 0 && (
              <p className="mt-1 text-[11px] text-amber-700">
                No companies yet — <a href="/companies" className="underline">add one first</a>.
              </p>
            )}
            {selectedCompany && (
              <p className="mt-1 text-[11px] text-zinc-500 leading-snug" data-testid="sched3-company-meta">
                Address: <b>{selectedCompany.registered_address || "— (add it in Company Details)"}</b><br />
                Auditor: <b>
                  {(selectedCompany.professionals || []).find(
                    (p) => (p.role || "").toLowerCase() === "statutory auditor"
                  )?.firm_name || "— (add Professional → Statutory Auditor)"}
                </b>
              </p>
            )}
          </div>
          <div>
            <label htmlFor="bs_date" className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
              Balance Sheet date <span className="text-red-500">*</span>
            </label>
            <input
              id="bs_date"
              data-testid="sched3-bs-date"
              value={bsDate}
              onChange={(e) => setBsDate(e.target.value)}
              placeholder="e.g. 31st March, 2026"
              className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none"
            />
          </div>
        </div>

        {/* Director picker — shown when a company is selected */}
        {selectedCompany && (selectedCompany.directors || []).length > 0 && (
          <div className="mt-5 border border-zinc-100 bg-zinc-50/50 px-4 py-3" data-testid="sched3-director-picker">
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-2">
              Signing directors — pick up to 2 ({selectedDirectorDins.length}/2 selected)
            </div>
            <div className="flex flex-wrap gap-2">
              {(selectedCompany.directors || []).map((d) => {
                const din = d.din || "";
                const checked = selectedDirectorDins.includes(din);
                const disabled = !checked && selectedDirectorDins.length >= 2;
                return (
                  <button
                    type="button"
                    key={din || d.name}
                    disabled={!din || disabled}
                    onClick={() => din && toggleDirector(din)}
                    data-testid={`sched3-dir-pill-${din || "noidx"}`}
                    className={
                      "px-3 py-1.5 text-xs border " +
                      (checked
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7] " +
                          (disabled ? "opacity-40 cursor-not-allowed" : ""))
                    }
                  >
                    {d.name}{din ? ` · DIN ${din}` : " · no DIN"}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Selected directors appear in the footer of every page in the generated workbook
              ("For and on behalf of the Board"). Auditor block on the right is pulled from
              <a href={`/companies/${companyId}`} className="text-[#002FA7] mx-1 inline-flex items-center gap-0.5 underline">
                Company Details · Professionals <Pencil size={9} />
              </a>.
            </p>
          </div>
        )}

        <div className="mt-6">
          <label htmlFor="tb_file" className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
            Trial Balance file — Current Year <span className="text-red-500">*</span>
          </label>
          <div className="border-2 border-dashed border-zinc-200 hover:border-[#002FA7] p-6 flex flex-col items-center justify-center transition-colors">
            <Upload size={20} className="text-zinc-400 mb-2" />
            <input
              id="tb_file"
              data-testid="sched3-tb-file"
              type="file"
              accept=".xlsx,.xls,.csv,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
            <p className="mt-2 text-[11px] text-zinc-500 text-center max-w-md">
              XLSX, XLS, CSV or PDF. The file must have columns recognisable as
              <b> Particulars / Ledger / Account</b> + <b>Debit</b>, <b>Credit</b> (or a
              <b> Closing Balance</b> column).
            </p>
            {file && (
              <p className="mt-2 text-[12px] text-[#002FA7] font-medium">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="prior_tb_file" className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">
            Trial Balance file — Previous Year <span className="text-zinc-400 normal-case tracking-normal text-[11px]">(optional · enables prior-year column + variance ratios)</span>
          </label>
          <div className="border-2 border-dashed border-zinc-200 hover:border-[#002FA7] p-4 flex flex-col items-center justify-center transition-colors">
            <input
              id="prior_tb_file"
              data-testid="sched3-prior-tb-file"
              type="file"
              accept=".xlsx,.xls,.csv,.pdf"
              onChange={(e) => setPriorFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
            {priorFile && (
              <p className="mt-2 text-[12px] text-[#002FA7] font-medium">
                Selected: {priorFile.name} ({(priorFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-zinc-100 pt-6">
          <button
            onClick={handleGenerate}
            disabled={busy}
            data-testid="sched3-generate-button"
            className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001E70] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            {busy ? "Classifying ledgers & rendering…" : "Generate Schedule III workbook"}
          </button>
        </div>

        {lastSummary && (
          <div className="mt-6 bg-blue-50/40 border border-blue-100 px-4 py-3 text-sm text-zinc-700">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#002FA7] font-medium mb-1">
              <Download size={11} /> Last run · 7 sheets generated
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div><span className="text-zinc-500">Ledgers (CY): </span><b>{lastSummary.rows}</b></div>
              <div><span className="text-zinc-500">PBT (CY): </span><b>₹{Number(lastSummary.pbt).toLocaleString("en-IN")}</b></div>
              <div><span className="text-zinc-500">PAT (CY): </span><b>₹{Number(lastSummary.pat).toLocaleString("en-IN")}</b></div>
              {Number(lastSummary.rowsPrev) > 0 && (
                <div><span className="text-zinc-500">Ledgers (PY): </span><b>{lastSummary.rowsPrev}</b></div>
              )}
              {Number(lastSummary.patPrev) !== 0 && (
                <>
                  <div><span className="text-zinc-500">PBT (PY): </span><b>₹{Number(lastSummary.pbtPrev).toLocaleString("en-IN")}</b></div>
                  <div><span className="text-zinc-500">PAT (PY): </span><b>₹{Number(lastSummary.patPrev).toLocaleString("en-IN")}</b></div>
                </>
              )}
            </div>
            <p className="mt-2 text-[11px] text-zinc-500">
              Open the downloaded workbook → check the <b>TB Classification</b> sheet to verify
              the mapping. <b>Ratios sheet</b> highlights any year-on-year variance &gt; 25 % in red —
              add the reason in column E to satisfy the Schedule III amendment 2021 disclosure.
              <b>Ageing sheets</b> are pre-built grids — fill them from your sub-ledgers; totals reconcile to the BS.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 text-[12px] text-zinc-500">
        <b className="text-zinc-700">Notes</b> · Division I (non-Ind-AS) format · most private
        limited companies use this. Division II (Ind-AS) and Division III (NBFC) are on the
        roadmap. The generated workbook contains formulas-free numbers — open in Excel and
        save as the final FY copy. Edit Notes to Accounts in-place if you need additional
        disclosures.
      </div>
    </div>
  );
}
